import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "../components/ui/dialog";
import { 
    Ticket, 
    SignOut, 
    ChartBar, 
    Plus,
    PencilSimple,
    Trash,
    UserCircle
} from "@phosphor-icons/react";

// Use relative URLs - works on both preview and production since /api is routed by ingress
const API = "";

function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

const UsersPage = () => {
    const { user, logout } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    // Dialog states
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    // Form states
    const [formData, setFormData] = useState({
        email: "",
        password: "",
        name: "",
        role: "user"
    });
    const [formError, setFormError] = useState("");
    const [formLoading, setFormLoading] = useState(false);

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const { data } = await axios.get(`${API}/api/users`, {
                withCredentials: true
            });
            setUsers(data || []);
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || "Failed to fetch users");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleLogout = async () => {
        await logout();
    };

    const openCreateDialog = () => {
        setFormData({ email: "", password: "", name: "", role: "user" });
        setFormError("");
        setShowCreateDialog(true);
    };

    const openEditDialog = (userToEdit) => {
        setSelectedUser(userToEdit);
        setFormData({
            email: userToEdit.email,
            password: "",
            name: userToEdit.name,
            role: userToEdit.role
        });
        setFormError("");
        setShowEditDialog(true);
    };

    const openDeleteDialog = (userToDelete) => {
        setSelectedUser(userToDelete);
        setShowDeleteDialog(true);
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        setFormError("");
        setFormLoading(true);

        try {
            await axios.post(`${API}/api/users`, formData, {
                withCredentials: true
            });
            setShowCreateDialog(false);
            fetchUsers();
        } catch (err) {
            setFormError(formatApiErrorDetail(err.response?.data?.detail) || "Failed to create user");
        } finally {
            setFormLoading(false);
        }
    };

    const handleUpdateUser = async (e) => {
        e.preventDefault();
        setFormError("");
        setFormLoading(true);

        try {
            const updateData = {
                email: formData.email,
                name: formData.name,
                role: formData.role
            };
            if (formData.password) {
                updateData.password = formData.password;
            }

            await axios.put(`${API}/api/users/${selectedUser.id}`, updateData, {
                withCredentials: true
            });
            setShowEditDialog(false);
            fetchUsers();
        } catch (err) {
            setFormError(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update user");
        } finally {
            setFormLoading(false);
        }
    };

    const handleDeleteUser = async () => {
        setFormLoading(true);
        try {
            await axios.delete(`${API}/api/users/${selectedUser.id}`, {
                withCredentials: true
            });
            setShowDeleteDialog(false);
            fetchUsers();
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || "Failed to delete user");
        } finally {
            setFormLoading(false);
        }
    };

    return (
        <div className="dashboard-layout">
            {/* Top Navigation */}
            <header className="flex items-center justify-between px-6 py-4 border-b border-border bg-white">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-[#002FA7] flex items-center justify-center">
                        <Ticket weight="bold" className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-lg font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        OPEN TASKS
                    </span>
                </div>

                <div className="flex items-center gap-4">
                    <Link to="/dashboard">
                        <Button 
                            variant="outline" 
                            data-testid="nav-dashboard-button"
                            className="h-9 border-2 hover:bg-[#002FA7] hover:text-white hover:border-[#002FA7] transition-colors duration-100"
                        >
                            <ChartBar weight="bold" className="w-4 h-4 mr-2" />
                            Dashboard
                        </Button>
                    </Link>
                    <div className="text-sm text-muted-foreground font-medium">
                        {user?.email}
                    </div>
                    <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleLogout}
                        data-testid="logout-button"
                        className="h-9 border-2 hover:bg-destructive hover:text-white hover:border-destructive transition-colors duration-100"
                    >
                        <SignOut weight="bold" className="w-4 h-4" />
                    </Button>
                </div>
            </header>

            {/* Main Content */}
            <main className="bg-[#F4F4F5] p-6">
                {/* Page Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                            USER MANAGEMENT
                        </h1>
                        <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mt-1">
                            Manage dashboard access
                        </p>
                    </div>
                    <Button
                        onClick={openCreateDialog}
                        data-testid="create-user-button"
                        className="h-10 bg-[#002FA7] hover:bg-[#002482] text-white font-bold uppercase tracking-widest text-xs border-0"
                    >
                        <Plus weight="bold" className="w-4 h-4 mr-2" />
                        Add User
                    </Button>
                </div>

                {error && (
                    <div 
                        data-testid="users-error-message"
                        className="mb-4 p-4 border border-destructive bg-destructive/5 text-destructive text-sm"
                    >
                        {error}
                    </div>
                )}

                {/* Users Table */}
                <div className="bg-white border border-border">
                    <Table className="ticket-table">
                        <TableHeader>
                            <TableRow className="bg-[#F4F4F5] hover:bg-[#F4F4F5]">
                                <TableHead className="text-xs font-bold uppercase tracking-widest py-3 px-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                    User
                                </TableHead>
                                <TableHead className="text-xs font-bold uppercase tracking-widest py-3 px-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                    Email
                                </TableHead>
                                <TableHead className="text-xs font-bold uppercase tracking-widest py-3 px-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                    Role
                                </TableHead>
                                <TableHead className="text-xs font-bold uppercase tracking-widest py-3 px-4 text-right" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                    Actions
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={4} className="text-center py-12">
                                        <span className="font-mono text-sm text-muted-foreground">
                                            LOADING USERS...
                                        </span>
                                    </TableCell>
                                </TableRow>
                            ) : users.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} className="text-center py-12">
                                        <div className="flex flex-col items-center gap-2">
                                            <UserCircle weight="light" className="w-12 h-12 text-muted-foreground" />
                                            <span className="text-sm text-muted-foreground">
                                                No users found
                                            </span>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                users.map((u) => (
                                    <TableRow 
                                        key={u.id} 
                                        data-testid={`user-row-${u.id}`}
                                        className="hover:bg-[#F4F4F5]/50"
                                    >
                                        <TableCell className="py-3 px-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 bg-[#F4F4F5] flex items-center justify-center border border-border">
                                                    <UserCircle weight="regular" className="w-5 h-5 text-muted-foreground" />
                                                </div>
                                                <span className="font-medium text-sm">{u.name}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell className="py-3 px-4 text-sm text-muted-foreground">
                                            {u.email}
                                        </TableCell>
                                        <TableCell className="py-3 px-4">
                                            <span className={`status-badge ${u.role === 'admin' ? 'status-open' : 'status-closed'}`}>
                                                {u.role}
                                            </span>
                                        </TableCell>
                                        <TableCell className="py-3 px-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => openEditDialog(u)}
                                                    data-testid={`edit-user-${u.id}`}
                                                    className="h-8 border-2"
                                                >
                                                    <PencilSimple weight="bold" className="w-4 h-4" />
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => openDeleteDialog(u)}
                                                    disabled={u.id === user?.id}
                                                    data-testid={`delete-user-${u.id}`}
                                                    className="h-8 border-2 hover:bg-destructive hover:text-white hover:border-destructive"
                                                >
                                                    <Trash weight="bold" className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </main>

            {/* Create User Dialog */}
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogContent className="border-2 shadow-none" data-testid="create-user-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-lg font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                            CREATE NEW USER
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateUser} className="space-y-4">
                        {formError && (
                            <div className="p-3 border border-destructive bg-destructive/5 text-destructive text-sm">
                                {formError}
                            </div>
                        )}
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                                Name
                            </Label>
                            <Input
                                data-testid="user-form-name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                required
                                className="h-10 border-2"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                                Email
                            </Label>
                            <Input
                                type="email"
                                data-testid="user-form-email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                required
                                className="h-10 border-2"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                                Password
                            </Label>
                            <Input
                                type="password"
                                data-testid="user-form-password"
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                required
                                className="h-10 border-2"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                                Role
                            </Label>
                            <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                                <SelectTrigger data-testid="user-form-role" className="h-10 border-2">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="user">User</SelectItem>
                                    <SelectItem value="admin">Admin</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <DialogFooter className="gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowCreateDialog(false)}
                                className="border-2"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={formLoading}
                                data-testid="user-form-submit"
                                className="bg-[#002FA7] hover:bg-[#002482] text-white border-0"
                            >
                                {formLoading ? "Creating..." : "Create User"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Edit User Dialog */}
            <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
                <DialogContent className="border-2 shadow-none" data-testid="edit-user-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-lg font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                            EDIT USER
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleUpdateUser} className="space-y-4">
                        {formError && (
                            <div className="p-3 border border-destructive bg-destructive/5 text-destructive text-sm">
                                {formError}
                            </div>
                        )}
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                                Name
                            </Label>
                            <Input
                                data-testid="user-form-name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                required
                                className="h-10 border-2"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                                Email
                            </Label>
                            <Input
                                type="email"
                                data-testid="user-form-email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                required
                                className="h-10 border-2"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                                New Password (optional)
                            </Label>
                            <Input
                                type="password"
                                data-testid="user-form-password"
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                placeholder="Leave blank to keep current"
                                className="h-10 border-2"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                                Role
                            </Label>
                            <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                                <SelectTrigger data-testid="user-form-role" className="h-10 border-2">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="user">User</SelectItem>
                                    <SelectItem value="admin">Admin</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <DialogFooter className="gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowEditDialog(false)}
                                className="border-2"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={formLoading}
                                data-testid="user-form-submit"
                                className="bg-[#002FA7] hover:bg-[#002482] text-white border-0"
                            >
                                {formLoading ? "Saving..." : "Save Changes"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete User Dialog */}
            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogContent className="border-2 shadow-none" data-testid="delete-user-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-lg font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                            DELETE USER
                        </DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground">
                        Are you sure you want to delete <strong>{selectedUser?.name}</strong> ({selectedUser?.email})?
                        This action cannot be undone.
                    </p>
                    <DialogFooter className="gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setShowDeleteDialog(false)}
                            className="border-2"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleDeleteUser}
                            disabled={formLoading}
                            data-testid="delete-user-confirm"
                            className="bg-destructive hover:bg-destructive/90 text-white border-0"
                        >
                            {formLoading ? "Deleting..." : "Delete User"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default UsersPage;
